/* eslint-disable max-lines */
/* eslint-disable complexity */
import {
  AfterViewInit,
  Directive,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  Renderer2,
  SimpleChanges
} from '@angular/core';

@Directive({
  selector: '[idpHeaderPosition]',
  standalone: true
})
export class HeaderPositionDirective
  implements OnDestroy, OnChanges, AfterViewInit
{
  private resizeObserver?: ResizeObserver;
  private mutationObserver?: MutationObserver;
  private scrollListener?: () => void;
  private isAtTop = true;
  private forceFixed = false;
  private lastScrollY = 0;
  @Input() selectedNavScroll = 0;
  @Input() selectedNav = '';
  @Input() selectedBookingTab = '';
  private scrollThreshold = window.innerHeight / 2;
  private updateTimeout?: number;
  private navChangeTime = 0;
  private tabChangeTime = 0;
  @Input() shouldShowMobileFullScreen = false;
  @Input() modifyButtonClicked = false;
  @Input() shopHeader = false;
  @Input() collapsedHeader = false;

  constructor(
    private el: ElementRef,
    private renderer: Renderer2
  ) {}

  ngAfterViewInit() {
    const isDesktop = window.innerWidth >= 992;
    this.isAtTop = isDesktop
      ? window.scrollY === 0
      : window.scrollY <= this.scrollThreshold;
    this.updatePosition();
    this.lastScrollY = window.scrollY;
    this.scrollListener = this.renderer.listen('window', 'scroll', () => {
      const isDesktop = window.innerWidth >= 992;
      this.isAtTop = isDesktop
        ? window.scrollY === 0
        : window.scrollY <= this.scrollThreshold;

      const timeSinceNavChange = Date.now() - this.navChangeTime;
      if (
        this.forceFixed &&
        window.scrollY > this.lastScrollY + 5 &&
        timeSinceNavChange > 500
      ) {
        this.forceFixed = false;
      }
      this.lastScrollY = window.scrollY;

      this.updateWidgetPosition();
    });
  }

  updatePosition(): void {
    requestAnimationFrame(() => {
      this.setupPositioning();
      this.observeHeightChanges();
    });
  }

  ngOnDestroy() {
    this.resizeObserver?.disconnect();
    this.mutationObserver?.disconnect();
    this.scrollListener?.();
    if (this.updateTimeout) {
      clearTimeout(this.updateTimeout);
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (
      changes['selectedNavScroll'] &&
      changes['selectedNavScroll'].currentValue
    ) {
      const isDesktop = window.innerWidth >= 992;
      if (isDesktop && window.scrollY > 0) {
        this.forceFixed = true;
        this.lastScrollY = window.scrollY;
        this.navChangeTime = Date.now();
      }
      requestAnimationFrame(() => {
        this.updateWidgetPosition();
      });
    }

    if (
      changes['selectedNav'] &&
      !changes['selectedNav'].firstChange &&
      changes['selectedNav'].currentValue
    ) {
      const isDesktop = window.innerWidth >= 992;
      if (isDesktop && window.scrollY > 0) {
        this.forceFixed = true;
        this.lastScrollY = window.scrollY;
        this.navChangeTime = Date.now();
      }
      requestAnimationFrame(() => {
        this.updateWidgetPosition();
      });
    }

    if (
      changes['selectedBookingTab'] &&
      !changes['selectedBookingTab'].firstChange
    ) {
      // Keep widget pinned when switching tabs while scrolled
      if (window.scrollY > 0 && window.innerWidth >= 992) {
        this.forceFixed = true;
        this.navChangeTime = Date.now();
      }
      this.tabChangeTime = Date.now();
      // Re-run after the new widget has had time to render and settle
      if (this.updateTimeout) {
        clearTimeout(this.updateTimeout);
      }
      this.updateTimeout = window.setTimeout(() => {
        this.updateWidgetPosition();
      }, 150);
    }

    if (changes['shouldShowMobileFullScreen']) {
      const isMobile = window.innerWidth < 992;
      if (isMobile && this.shouldShowMobileFullScreen) {
        this.renderer.setStyle(document.body, 'overflow', 'hidden');
        this.renderer.setStyle(document.body, 'position', 'fixed');
        this.renderer.setStyle(document.body, 'width', '100%');
        this.renderer.setStyle(document.body, 'height', '100%');
      } else {
        this.renderer.removeStyle(document.body, 'overflow');
        this.renderer.removeStyle(document.body, 'position');
        this.renderer.removeStyle(document.body, 'width');
        this.renderer.removeStyle(document.body, 'height');
      }
      this.updateWidgetPosition();
    }
  }

  private setupPositioning() {
    this.updateWidgetPosition();
  }

  private observeHeightChanges() {
    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => {
        const timeSinceTabChange = Date.now() - this.tabChangeTime;
        const suppressed = timeSinceTabChange < 150;
        if (suppressed) return;
        if (this.updateTimeout) {
          clearTimeout(this.updateTimeout);
        }
        this.updateTimeout = window.setTimeout(() => {
          this.updateWidgetPosition();
        }, 10);
      });

      const elements = [
        '.header__top',
        '.widgets-container__nav-links',
        '.header__booking'
      ];
      elements.forEach((selector) => {
        const element = this.el.nativeElement.querySelector(selector);
        if (element) this.resizeObserver?.observe(element);
      });

      const alertAdvisories = document.querySelector(
        '.idp-alert-advisories-container'
      );
      if (alertAdvisories) this.resizeObserver?.observe(alertAdvisories);
    }

    this.mutationObserver = new MutationObserver((mutations) => {
      const isRelevant = mutations.some((m) => {
        const target = m.target as HTMLElement;
        return (
          this.el.nativeElement.contains(target) ||
          target.closest?.('.idp-alert-advisories-container') !== null
        );
      });
      if (!isRelevant) return;

      // If header__booking just appeared in the DOM, start observing it for resize
      if (this.resizeObserver) {
        mutations.forEach((m) => {
          m.addedNodes.forEach((node) => {
            if (node instanceof HTMLElement) {
              const booking = node.classList?.contains('header__booking')
                ? node
                : node.querySelector?.('.header__booking');
              if (booking) this.resizeObserver?.observe(booking);
            }
          });
        });
      }
      if (this.updateTimeout) {
        clearTimeout(this.updateTimeout);
      }
      this.updateTimeout = window.setTimeout(() => {
        this.updateWidgetPosition();
      }, 10);
    });
    this.mutationObserver.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  private updateWidgetPosition() {
    const isDesktop = window.innerWidth >= 992;
    const headerWidgets =
      this.el.nativeElement.querySelector('.header__widgets');
    const navLinks = this.el.nativeElement.querySelector(
      '.widgets-container__nav-links'
    );
    const headerTop = this.el.nativeElement.querySelector('.header__top');
    const headerBooking =
      this.el.nativeElement.querySelector('.header__booking');
    const secondaryNav = this.el.nativeElement.querySelector(
      'idp-secondary-navigation-links'
    );
    const alertAdvisories = document.querySelector(
      '.idp-alert-advisories-container'
    );

    if (alertAdvisories) {
      this.renderer.setStyle(alertAdvisories, 'top', '0');
      this.renderer.setStyle(alertAdvisories, 'z-index', '1030');
    }

    const alertHeight = alertAdvisories
      ? (alertAdvisories as HTMLElement).offsetHeight
      : 0;

    if (headerTop) {
      // Always use fixed positioning for header__top to keep it at the top when scrolling
      this.renderer.setStyle(headerTop, 'position', 'fixed');
      this.renderer.setStyle(headerTop, 'top', `${alertHeight}px`);
      this.renderer.setStyle(headerTop, 'z-index', '1040');

      // When collapsedHeader is true, add margin to the nav element to push content below the fixed header
      if (this.collapsedHeader) {
        const headerTopHeight = headerTop.offsetHeight;
        // On mobile, include navLinks height only when at top (navLinks is visible) and not on advance search page
        const hasAdvanceSearch = !!document.querySelector('idp-advance-search');
        const navLinksHeight =
          !isDesktop && navLinks && this.isAtTop && !hasAdvanceSearch
            ? navLinks.offsetHeight
            : 0;
        this.renderer.setStyle(
          this.el.nativeElement,
          'margin-top',
          `${alertHeight + headerTopHeight + navLinksHeight}px`
        );
      }
    }

    // Position secondary navigation below header__top when not at top
    if (secondaryNav && headerTop && !this.isAtTop) {
      const headerTopHeight = Math.floor(headerTop.offsetHeight);
      this.renderer.setStyle(secondaryNav, 'position', 'fixed');
      this.renderer.setStyle(
        secondaryNav,
        'top',
        `${alertHeight + headerTopHeight}px`
      );
      this.renderer.setStyle(secondaryNav, 'left', '0');
      this.renderer.setStyle(secondaryNav, 'right', '0');
      this.renderer.setStyle(secondaryNav, 'z-index', '12');
    } else if (secondaryNav && !this.collapsedHeader) {
      this.renderer.removeStyle(secondaryNav, 'position');
      this.renderer.removeStyle(secondaryNav, 'top');
      this.renderer.removeStyle(secondaryNav, 'left');
      this.renderer.removeStyle(secondaryNav, 'right');
      this.renderer.removeStyle(secondaryNav, 'z-index');
    }

    if (!headerWidgets) return;

    // Desktop positioning - always show booking and widgets
    if (isDesktop) {
      const useFixed = this.forceFixed && !this.isAtTop;
      this.renderer.setStyle(headerWidgets, 'z-index', '12');

      // Position header__booking below header__top on desktop
      if (headerBooking && headerTop) {
        this.renderer.removeStyle(headerBooking, 'display');
        const headerTopHeight = Math.floor(headerTop.offsetHeight) - 1;
        const bookingPosition = useFixed ? 'fixed' : 'sticky';
        const bookingTop = useFixed
          ? `${alertHeight + headerTopHeight + 1}px`
          : `${headerTopHeight + 1}px`;

        this.renderer.setStyle(headerBooking, 'position', bookingPosition);
        this.renderer.setStyle(headerBooking, 'top', bookingTop);

        // Always set margin-top for sticky, remove for fixed
        if (useFixed) {
          this.renderer.setStyle(headerBooking, 'margin-top', '0');
        } else {
          this.renderer.setStyle(
            headerBooking,
            'margin-top',
            `${alertHeight + headerTopHeight}px`
          );
        }
        this.renderer.setStyle(headerBooking, 'left', '0');
        this.renderer.setStyle(headerBooking, 'right', '0');
        this.renderer.setStyle(headerBooking, 'z-index', '12');
      }
      // Position header__widgets below header__booking or header__top on desktop
      if (headerTop) {
        const widgetsPosition = this.resolveWidgetsPosition(
          secondaryNav,
          useFixed
        );
        this.renderer.setStyle(headerWidgets, 'position', widgetsPosition);
        this.renderer.setStyle(headerWidgets, 'left', '0');
        this.renderer.setStyle(headerWidgets, 'right', '0');

        if (this.collapsedHeader && secondaryNav) {
          const headerTopHeight = Math.floor(headerTop.offsetHeight);
          this.renderer.setStyle(
            headerWidgets,
            'top',
            `${alertHeight + headerTopHeight}px`
          );
          this.renderer.setStyle(headerWidgets, 'margin-top', '0');
        } else if (!headerBooking) {
          const headerTopHeight = Math.floor(headerTop.offsetHeight) - 1;
          const widgetsTop = this.shopHeader
            ? '0'
            : useFixed
              ? `${alertHeight + headerTopHeight + 1}px`
              : `${headerTopHeight + 1}px`;

          this.renderer.setStyle(headerWidgets, 'top', widgetsTop);
          if (useFixed) {
            this.renderer.setStyle(headerWidgets, 'margin-top', '0');
          } else {
            const marginTop =
              this.shopHeader && !this.modifyButtonClicked
                ? '0'
                : `${alertHeight + headerTopHeight}px`;

            this.renderer.setStyle(headerWidgets, 'margin-top', marginTop);
          }
        } else if (headerBooking) {
          const bookingHeight = Math.floor(headerBooking.offsetHeight);
          const widgetsTop = useFixed
            ? `${
                alertHeight + Math.floor(headerTop.offsetHeight) + bookingHeight
              }px`
            : `${Math.floor(headerTop.offsetHeight) + bookingHeight}px`;

          this.renderer.setStyle(headerWidgets, 'top', widgetsTop);
          this.renderer.setStyle(headerWidgets, 'margin-top', '0');
        }
      }
      return;
    }

    // Mobile positioning - hide when not at top
    if (!this.isAtTop) {
      if (navLinks) {
        this.renderer.setStyle(navLinks, 'display', 'none');
      }
      return;
    }

    if (navLinks && headerTop) {
      this.renderer.removeStyle(navLinks, 'display');
      const headerTopHeight = Math.floor(headerTop.offsetHeight) + alertHeight;
      this.renderer.setStyle(navLinks, 'position', 'fixed');
      this.renderer.setStyle(navLinks, 'top', `${headerTopHeight - 1}px`);
      this.renderer.setStyle(navLinks, 'left', '0');
      this.renderer.setStyle(navLinks, 'right', '0');
      const mobileNavElement = document.getElementById(
        'mach-core-mobile-nav-primary'
      );
      if (mobileNavElement) {
        this.renderer.setStyle(navLinks, 'padding', '16px 0');
      } else {
        this.renderer.setStyle(navLinks, 'padding', '0 0');
      }

      if (this.shouldShowMobileFullScreen) {
        this.renderer.setStyle(navLinks, 'z-index', '12');
        this.renderer.setStyle(headerTop, 'z-index', '12');
      } else {
        this.renderer.removeStyle(navLinks, 'z-index');
        this.renderer.removeStyle(headerTop, 'z-index');
      }
    }

    // Position header__booking below navLinks in mobile
    if (headerBooking && navLinks && headerTop) {
      const navRect = navLinks.getBoundingClientRect();
      const bookingMarginTop = Math.floor(navRect.bottom) - 1;
      const bookingTop =
        alertHeight +
        Math.floor(headerTop.offsetHeight) +
        Math.floor(navLinks.offsetHeight);
      const bookingPosition = this.shouldShowMobileFullScreen
        ? 'fixed'
        : 'sticky';
      this.renderer.setStyle(headerBooking, 'position', bookingPosition);
      this.renderer.setStyle(headerBooking, 'top', `${bookingTop - 1}px`);
      this.renderer.setStyle(
        headerBooking,
        'margin-top',
        this.shouldShowMobileFullScreen ? '0' : `${bookingMarginTop}px`
      );
      this.renderer.setStyle(headerBooking, 'left', '0');
      this.renderer.setStyle(headerBooking, 'right', '0');
    }

    const widgetsPosition = this.shouldShowMobileFullScreen
      ? 'fixed'
      : 'sticky';
    this.renderer.setStyle(headerWidgets, 'position', widgetsPosition);
    if (this.shouldShowMobileFullScreen) {
      const isSafariMobile =
        /Safari/.test(navigator.userAgent) &&
        /Mobile/.test(navigator.userAgent);
      const zIndex = isSafariMobile ? '1040' : '12';
      this.renderer.setStyle(headerWidgets, 'z-index', zIndex);
    } else {
      this.renderer.removeStyle(headerWidgets, 'z-index');
    }

    if (navLinks && headerTop) {
      const widgetsTop =
        alertHeight +
        Math.floor(headerTop.offsetHeight) +
        Math.floor(navLinks.offsetHeight) +
        (headerBooking ? Math.floor(headerBooking.offsetHeight) : 0);

      this.renderer.setStyle(headerWidgets, 'top', `${widgetsTop - 1}px`);

      if (!headerBooking && !this.shouldShowMobileFullScreen) {
        const navRect = navLinks.getBoundingClientRect();
        const widgetsMarginTop = Math.floor(navRect.bottom) - 1;
        this.renderer.setStyle(
          headerWidgets,
          'margin-top',
          `${widgetsMarginTop}px`
        );
      } else if (this.shouldShowMobileFullScreen) {
        this.renderer.setStyle(headerWidgets, 'margin-top', '0');
        if (headerBooking) {
          this.renderer.setStyle(headerBooking, 'z-index', '12');
        }
      } else {
        this.renderer.removeStyle(headerWidgets, 'margin-top');
      }
    }

    this.renderer.setStyle(headerWidgets, 'left', '0');
    this.renderer.setStyle(headerWidgets, 'right', '0');
  }

  private resolveWidgetsPosition(
    secondaryNav: Element | null,
    useFixed: boolean
  ): string {
    if (this.collapsedHeader && secondaryNav) return 'fixed';
    if (this.shopHeader) return 'absolute';
    return useFixed ? 'fixed' : 'sticky';
  }
}
